import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, AlertCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  wbTemplatesApi,
  type WbTemplateCatalogEntry,
  type WbInstantiateEvent,
  type WbBusinessInfo,
  type WbInstantiateBody,
} from '@/lib/api/services/websiteBuilder';
import {
  organizationApi,
  type OrganizationAiContextResponse,
} from '@/lib/api/services/organization';
import { useAuth } from '@/components/AuthProvider';
import { TemplateCard } from './templates/TemplateCard';
import { MiniForm } from './templates/MiniForm';
import { InstantiateProgress } from './templates/InstantiateProgress';

interface TemplateGalleryProps {
  /** When set, instantiate adds pages to this existing site instead of
   *  creating a new one. The instantiate body forwards `addToSiteId` and
   *  the editor "+ New page from template" flow lands here. */
  addToSiteId?: string;
  /** Called with the new site+page on a successful instantiate. The caller
   *  navigates the user to the editor (or the new page if addToSiteId was
   *  set). */
  onComplete: (siteId: string, firstPageId: string) => void;
  /** Closing the gallery — present only when the gallery is rendered inside
   *  a modal (returning-user + addToSiteId paths). Omitted on the empty-state
   *  dashboard path where there's nowhere else for the user to go. */
  onClose?: () => void;
  /** Escape hatch from the empty-state gallery — invoked when the user
   *  chooses "Start with a blank site" instead of any template. Present
   *  only on the first-time / dashboard empty-state mount; omitted in
   *  modal + addToSiteId paths where the user already has a site and the
   *  blank-site CTA lives on the dashboard itself. */
  onStartBlank?: () => void;
}

/**
 * Determines whether the existing AI Knowledge is sufficient to skip the
 * mini-form. Per spec §11.1 / §11.3: only `business_name` is hard-required
 * upstream of the instantiate API, so its presence is the single gate.
 */
function isAiContextSufficient(
  ctx: OrganizationAiContextResponse | null,
): boolean {
  if (!ctx) return false;
  if (!ctx.hasContent) return false;
  const name = (ctx.parsed?.business_name ?? '').trim();
  return name.length > 0;
}

/**
 * Build the lockedFields prop for MiniForm — fields already populated in
 * AI Knowledge come pre-filled and disabled with a "managed in Settings"
 * hint, so this in-flow form can never clobber an admin-set value
 * (spec §11.2).
 */
function deriveLockedFields(
  ctx: OrganizationAiContextResponse | null,
): Array<keyof WbBusinessInfo> {
  if (!ctx?.parsed) return [];
  const p = ctx.parsed;
  const locked: Array<keyof WbBusinessInfo> = [];
  if ((p.business_name ?? '').trim()) locked.push('business_name');
  if ((p.services ?? '').trim()) locked.push('services');
  if ((p.support_phone ?? '').trim()) locked.push('support_phone');
  if ((p.support_email ?? '').trim()) locked.push('support_email');
  if ((p.instagram_handle ?? '').trim()) locked.push('instagram_handle');
  if ((p.website ?? '').trim()) locked.push('website');
  // business_type is NOT in OrganizationAiContextStructured (it isn't a
  // serialized field — only a hint we collect via mini-form to drive the
  // Recommended sort). So we never lock it here.
  return locked;
}

/**
 * Pick a grid class that adapts to the number of cards. Avoids the
 * "one lonely card on the left of a 3-col grid" look when only 1–2
 * templates exist (the production state when the catalog has just been
 * seeded with one or two templates).
 *
 * - 1 card  → max-w-sm with mx-auto so the single card sits centered and
 *             readable rather than pinned-left at 1/3 width
 * - 2 cards → 1-col on mobile, 2-col from md+. Skip the 3-col jump so
 *             cards stay generously sized rather than shrinking on lg
 * - 3+      → existing responsive 1/2/3-col ladder
 */
function gridClassFor(count: number): string {
  if (count <= 1) {
    return 'grid grid-cols-1 gap-6 max-w-sm mx-auto';
  }
  if (count === 2) {
    return 'grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto';
  }
  return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';
}

/**
 * Initial values for the MiniForm prefill, derived from the org's AI
 * Knowledge. business_type is intentionally blank — see deriveLockedFields.
 */
function deriveInitialValues(
  ctx: OrganizationAiContextResponse | null,
): WbBusinessInfo {
  if (!ctx?.parsed) {
    return {
      business_name: '',
      business_type: '',
      services: '',
      support_phone: '',
      support_email: '',
      instagram_handle: '',
      website: '',
    };
  }
  const p = ctx.parsed;
  return {
    business_name: p.business_name ?? '',
    business_type: '',
    services: p.services ?? '',
    support_phone: p.support_phone ?? '',
    support_email: p.support_email ?? '',
    instagram_handle: p.instagram_handle ?? '',
    website: p.website ?? '',
  };
}

/**
 * Resolves the CDN prefix that template thumbnails are served from. Thin
 * wrapper over `resolveTemplateThumbnailUrl('')`-equivalent so the helper
 * stays the single source of truth for thumbnail URL construction across
 * the gallery + admin panel + any future surface.
 *
 * Resolution order: VITE_CDN_URL → backend proxy → empty. The catalog
 * field `template.thumbnail` ALWAYS holds the S3 key, never an absolute
 * URL, so the concatenation `${baseUrl}${template.thumbnail}` works
 * regardless of which resolution branch wins.
 */
function resolveThumbnailBaseUrl(): string {
  const env = (import.meta as any).env ?? {};
  if (env.VITE_CDN_URL) {
    return `${String(env.VITE_CDN_URL).replace(/\/+$/, '')}/`;
  }
  const apiBase = (env.VITE_API_BASE_URL || '/api/v1').replace(/\/+$/, '');
  return `${apiBase}/wb/template-asset/`;
}

interface TemplateGalleryState {
  catalogLoading: boolean;
  catalogError: string | null;
  templates: WbTemplateCatalogEntry[];
  contextLoading: boolean;
  aiContext: OrganizationAiContextResponse | null;
  // Mini-form modal — open with the chosen template id when AI Knowledge
  // is insufficient AND the user clicks a card.
  miniFormTemplateId: string | null;
  // Instantiate overlay — `templateId` is held so retry can fire with the
  // exact same payload as the original attempt.
  instantiateTemplateId: string | null;
  instantiateBody: WbInstantiateBody | null;
  events: WbInstantiateEvent[];
  status: 'idle' | 'streaming' | 'done' | 'error';
  errorMessage?: string;
}

/**
 * Template Gallery page (spec §7). Rendered as the website-builder dashboard
 * when wb_sites.count === 0 for the org, and inside a modal via "+ New site
 * from template" / "+ New page from template" otherwise.
 *
 * Flow:
 *   1. On mount: fetch catalog + AI context in parallel.
 *   2. Compute "is AI Knowledge sufficient?" (only business_name is hard-
 *      required, per spec §11.1).
 *   3. Sort cards — templates whose business_types[] includes the user's
 *      business_type bubble to the top with a Recommended badge.
 *   4. Card click:
 *      - If AI Knowledge sufficient → fire instantiate immediately.
 *      - Else → open mini-form modal first. On submit, fire instantiate.
 *   5. Render <InstantiateProgress /> overlay while streaming. On done →
 *      onComplete after 500ms (lets the user see the final check).
 *
 * Spec §11.21 (user abandons mid-instantiate): server keeps running; the
 * caller's onComplete navigates to the new editor. If the user closes the
 * tab the server's watchdog rolls back if it can't finish.
 *
 * Spec §11.32 (browser back during streaming): `beforeunload` listener
 * prompts "Are you sure?" while status === 'streaming'. Removed on
 * done/error/unmount.
 */
const TemplateGallery: React.FC<TemplateGalleryProps> = ({
  addToSiteId,
  onComplete,
  onClose,
  onStartBlank,
}) => {
  // Defensive auth context read — mirrors MiniForm. If the gallery is ever
  // mounted outside AuthProvider (tests, error pages), the admin gate
  // defaults to false; the server is the authoritative gate.
  const auth = (() => {
    try {
      return useAuth();
    } catch {
      return null;
    }
  })();
  const isAdmin = (() => {
    const u = auth?.user;
    if (!u) return false;
    if (u.is_super_admin === true) return true;
    const role = (u.role ?? '').toString().trim().toUpperCase();
    return role === 'ADMIN';
  })();

  const [state, setState] = useState<TemplateGalleryState>({
    catalogLoading: true,
    catalogError: null,
    templates: [],
    contextLoading: true,
    aiContext: null,
    miniFormTemplateId: null,
    instantiateTemplateId: null,
    instantiateBody: null,
    events: [],
    status: 'idle',
    errorMessage: undefined,
  });

  // Used to defer the onComplete callback by 500ms after the `done` event
  // so the user gets a beat of "✓ Saved" feedback before the editor opens.
  const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const thumbnailBaseUrl = useMemo(() => resolveThumbnailBaseUrl(), []);

  // ── catalog + ai-context fetch (parallel) ────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setState((s) => ({
      ...s,
      catalogLoading: true,
      contextLoading: true,
      catalogError: null,
    }));

    void Promise.allSettled([
      wbTemplatesApi.list(),
      organizationApi.getAiContext(),
    ]).then(([catalogRes, ctxRes]) => {
      if (cancelled) return;
      // Catalog — apiClient response shape is { data, error }. The actual
      // payload may be at res.data.data (when the backend wraps in a
      // { success, data } envelope) or just res.data — same shape as the
      // existing dashboard's loadSites().
      let templates: WbTemplateCatalogEntry[] = [];
      let catalogError: string | null = null;
      if (catalogRes.status === 'fulfilled') {
        const r: any = catalogRes.value;
        if (r?.error) {
          catalogError = r.error;
        } else {
          const payload = r?.data?.data ?? r?.data ?? r;
          if (Array.isArray(payload)) templates = payload;
          else if (Array.isArray(payload?.templates)) templates = payload.templates;
        }
      } else {
        catalogError =
          catalogRes.reason instanceof Error
            ? catalogRes.reason.message
            : 'Failed to load templates';
      }

      let aiContext: OrganizationAiContextResponse | null = null;
      if (ctxRes.status === 'fulfilled' && ctxRes.value.ok) {
        aiContext = ctxRes.value.data;
      }
      // ctxRes.ok === false is non-fatal: treat as "no content" so we
      // surface the mini-form rather than blocking the whole gallery.

      setState((s) => ({
        ...s,
        catalogLoading: false,
        contextLoading: false,
        catalogError,
        templates,
        aiContext,
      }));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // ── beforeunload guard while streaming (spec §11.32) ─────────────────
  useEffect(() => {
    if (state.status !== 'streaming') return;
    const handler = (e: BeforeUnloadEvent) => {
      // Modern browsers ignore the custom message but still honor
      // preventDefault to show the generic prompt.
      e.preventDefault();
      // Legacy compat — Chrome <51 / older Firefox.
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [state.status]);

  // ── clean up the completion timer on unmount ─────────────────────────
  useEffect(() => {
    return () => {
      if (completionTimerRef.current) {
        clearTimeout(completionTimerRef.current);
        completionTimerRef.current = null;
      }
    };
  }, []);

  // ── recommended-sort ordering ────────────────────────────────────────
  const businessType = (state.aiContext?.parsed as any)?.business_type ?? null;
  // Note: business_type isn't a serialized AI Knowledge field today, so this
  // is null on every read. It IS captured in the mini-form payload though,
  // so once the mini-form has run + the merge has written, a subsequent
  // gallery render will get it back. For first-time empty-state flow we
  // just show all templates in catalog order.

  const { recommended, others } = useMemo(() => {
    const rec: WbTemplateCatalogEntry[] = [];
    const oth: WbTemplateCatalogEntry[] = [];
    if (!businessType) {
      return { recommended: [], others: state.templates };
    }
    for (const t of state.templates) {
      if (Array.isArray(t.business_types) && t.business_types.includes(businessType)) {
        rec.push(t);
      } else {
        oth.push(t);
      }
    }
    return { recommended: rec, others: oth };
  }, [state.templates, businessType]);

  // ── card click → instantiate or mini-form ────────────────────────────
  const handleCardSelect = useCallback(
    (template: WbTemplateCatalogEntry) => {
      if (isAiContextSufficient(state.aiContext)) {
        // AI Knowledge has at least business_name — fire instantiate
        // directly. No mini-form needed.
        const body: WbInstantiateBody = {
          siteName: state.aiContext?.parsed?.business_name?.trim() || undefined,
          ...(addToSiteId ? { addToSiteId } : {}),
        };
        startInstantiate(template.id, body);
        return;
      }
      // Not sufficient — open mini-form. The form will hand back the
      // businessInfo payload and we'll fire instantiate from there.
      setState((s) => ({ ...s, miniFormTemplateId: template.id }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.aiContext, addToSiteId],
  );

  const handleMiniFormSubmit = useCallback(
    (values: WbBusinessInfo) => {
      const templateId = state.miniFormTemplateId;
      if (!templateId) return;
      const body: WbInstantiateBody = {
        siteName: values.business_name?.trim() || undefined,
        businessInfo: values,
        ...(addToSiteId ? { addToSiteId } : {}),
      };
      setState((s) => ({ ...s, miniFormTemplateId: null }));
      startInstantiate(templateId, body);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.miniFormTemplateId, addToSiteId],
  );

  const startInstantiate = useCallback(
    (templateId: string, body: WbInstantiateBody) => {
      setState((s) => ({
        ...s,
        instantiateTemplateId: templateId,
        instantiateBody: body,
        events: [],
        status: 'streaming',
        errorMessage: undefined,
      }));

      void wbTemplatesApi.instantiateStream(
        templateId,
        body,
        // onEvent — append every well-formed event in arrival order.
        (event) => {
          setState((s) => ({ ...s, events: [...s.events, event] }));
          if (event.type === 'done') {
            // Flip status to done immediately so the overlay renders the
            // "Opening editor…" final step, then call onComplete after 500ms.
            const siteId = event.site_id;
            const firstPageId = event.first_page_id;
            setState((s) => ({ ...s, status: 'done' }));
            if (completionTimerRef.current) {
              clearTimeout(completionTimerRef.current);
            }
            completionTimerRef.current = setTimeout(() => {
              completionTimerRef.current = null;
              onComplete(siteId, firstPageId);
            }, 500);
          }
        },
        // onDone — terminal [DONE] sentinel. If we already saw a `done`
        // event the status is already 'done' and the timer is queued; if
        // not (server closed early with no error), flip to done so the
        // overlay doesn't spin forever — onComplete cannot fire because we
        // don't have the ids.
        () => {
          setState((s) =>
            s.status === 'streaming' ? { ...s, status: 'done' } : s,
          );
        },
        // onError — surfaces server-emitted error events and network
        // failures alike (apiClient swallow-error pattern — see spec).
        (msg) => {
          setState((s) => ({
            ...s,
            status: 'error',
            errorMessage: msg || 'Something went wrong',
          }));
        },
      );
    },
    [onComplete],
  );

  const handleRetry = useCallback(() => {
    const { instantiateTemplateId, instantiateBody } = state;
    if (!instantiateTemplateId || !instantiateBody) return;
    startInstantiate(instantiateTemplateId, instantiateBody);
  }, [state, startInstantiate]);

  const handlePickDifferent = useCallback(() => {
    if (completionTimerRef.current) {
      clearTimeout(completionTimerRef.current);
      completionTimerRef.current = null;
    }
    setState((s) => ({
      ...s,
      instantiateTemplateId: null,
      instantiateBody: null,
      events: [],
      status: 'idle',
      errorMessage: undefined,
    }));
  }, []);

  // ── render ───────────────────────────────────────────────────────────
  const isLoading = state.catalogLoading || state.contextLoading;
  const showOverlay = state.status === 'streaming' || state.status === 'error';
  // Note: status === 'done' also briefly renders the overlay until onComplete
  // fires the navigation — but we keep it under showOverlay so the user sees
  // the final check before redirect.
  const showDoneOverlay = state.status === 'done';

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="wb-template-gallery">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {addToSiteId ? 'Add a page from a template' : 'Pick a template to get started'}
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            Personalized with your business info — ready to edit in seconds.
          </p>
          {/* Empty-state escape hatch — only rendered when the parent passed
              onStartBlank (i.e. dashboard mounted the gallery directly, not
              as a modal). Lets a first-time user skip templates and go
              straight to the blank-site dialog. Disabled while an
              instantiate stream is in flight so the user can't switch
              flows mid-SSE. */}
          {onStartBlank && (
            <button
              type="button"
              onClick={onStartBlank}
              disabled={state.status === 'streaming'}
              data-testid="wb-template-gallery-start-blank"
              className="mt-3 text-sm font-medium text-gray-600 hover:text-gray-900 underline underline-offset-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Or start with a blank site →
            </button>
          )}
        </div>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="wb-template-gallery-close"
            aria-label="Close gallery"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Loading skeleton — 6 placeholder cards while catalog + context resolve. */}
      {isLoading && (
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          data-testid="wb-template-gallery-skeleton"
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="rounded-xl border border-gray-200 bg-gray-100 h-72 animate-pulse"
              data-testid="wb-template-gallery-skeleton-card"
            />
          ))}
        </div>
      )}

      {/* Catalog fetch failed — show error + retry. */}
      {!isLoading && state.catalogError && (
        <div
          className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700 flex items-start gap-3"
          data-testid="wb-template-gallery-error"
        >
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-medium">Could not load templates</div>
            <div className="text-xs mt-1">{state.catalogError}</div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.reload()}
            data-testid="wb-template-gallery-error-retry"
          >
            Retry
          </Button>
        </div>
      )}

      {/* Empty catalog — server returned [] (e.g. all templates disabled). */}
      {!isLoading && !state.catalogError && state.templates.length === 0 && (
        <div
          className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl"
          data-testid="wb-template-gallery-empty"
        >
          <h3 className="text-base font-semibold text-gray-700">
            No templates available yet
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Check back soon — we're adding new ones every week.
          </p>
        </div>
      )}

      {/* Recommended section — only when business_type is known AND there's
          at least one match. */}
      {!isLoading && !state.catalogError && recommended.length > 0 && (
        <section className="mb-8" data-testid="wb-template-gallery-recommended-section">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">
            Recommended for your business
          </h2>
          <div className={gridClassFor(recommended.length)}>
            {recommended.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                thumbnailBaseUrl={thumbnailBaseUrl}
                isRecommended={true}
                onSelect={handleCardSelect}
              />
            ))}
          </div>
        </section>
      )}

      {/* Other templates — when business_type is known + we have recommended,
          this is "Show all" section. When business_type is unknown, this IS
          the gallery. */}
      {!isLoading && !state.catalogError && others.length > 0 && (
        <section data-testid="wb-template-gallery-all-section">
          {recommended.length > 0 && (
            <h2 className="text-sm font-semibold text-gray-700 mb-3">
              All templates
            </h2>
          )}
          <div className={gridClassFor(others.length)}>
            {others.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                thumbnailBaseUrl={thumbnailBaseUrl}
                isRecommended={false}
                onSelect={handleCardSelect}
              />
            ))}
          </div>
        </section>
      )}

      {/* Mini-form modal — opens on card click when AI Knowledge is empty
          or missing business_name. */}
      {state.miniFormTemplateId && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          data-testid="wb-template-gallery-mini-form-modal"
        >
          <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl p-5 my-8">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Tell us about your business
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  We'll use this to personalize your template.
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setState((s) => ({ ...s, miniFormTemplateId: null }))}
                aria-label="Close form"
                data-testid="wb-template-gallery-mini-form-close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <MiniForm
              initialValues={deriveInitialValues(state.aiContext)}
              lockedFields={deriveLockedFields(state.aiContext)}
              isAdmin={isAdmin}
              onSubmit={handleMiniFormSubmit}
            />
          </div>
        </div>
      )}

      {/* Instantiate overlay — covers everything while SSE is in flight or
          terminated with an error. */}
      {(showOverlay || showDoneOverlay) && (
        <InstantiateProgress
          events={state.events}
          status={state.status === 'idle' ? 'streaming' : state.status}
          errorMessage={state.errorMessage}
          onRetry={handleRetry}
          onPickDifferent={handlePickDifferent}
        />
      )}
    </div>
  );
};

export default TemplateGallery;
